"use client";

import {
  hasGoogleMapsKey,
  loadPlacesLibrary,
} from "@/lib/geocoding/google-maps-loader";
import {
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { ErrorBoundary } from "@/components/error/error-boundary";
import { useHasMounted } from "@/hooks/use-has-mounted";

type PlacesStatus = "idle" | "loading" | "loaded" | "failed" | "unavailable";

interface PlacesContextValue {
  placesLib: google.maps.PlacesLibrary | null;
  status: PlacesStatus;
  requestLoad: (() => void) | null;
}

const PlacesContext = createContext<PlacesContextValue>({
  placesLib: null,
  status: "idle",
  requestLoad: null,
});

interface PlacesProviderProps {
  children: ReactNode;
  fallback?: ReactNode;
  deferred?: boolean;
}

type LoadOutcome =
  | { kind: "pending" }
  | { kind: "loaded"; placesLib: google.maps.PlacesLibrary }
  | { kind: "failed" };

function warnPlacesUnavailable() {
  console.warn(
    "[places] Address autocomplete unavailable — falling back to manual entry. " +
    "Check NEXT_PUBLIC_GOOGLE_MAPS_API_KEY referrer restrictions or network blockers.",
  );
}

function resolveStatus(outcome: LoadOutcome, isRequested: boolean): PlacesStatus {
  if (!hasGoogleMapsKey()) return "unavailable";
  if (outcome.kind !== "pending") return outcome.kind;
  return isRequested ? "loading" : "idle";
}

function usePlacesLibrary(shouldLoad: boolean): LoadOutcome {
  const [outcome, setOutcome] = useState<LoadOutcome>({ kind: "pending" });

  useEffect(() => {
    if (!shouldLoad) return;
    if (!hasGoogleMapsKey()) {
      warnPlacesUnavailable();
      return;
    }
    let cancelled = false;
    loadPlacesLibrary().then(
      (placesLib) => {
        if (!cancelled) setOutcome({ kind: "loaded", placesLib });
      },
      (error: unknown) => {
        if (cancelled) return;
        console.warn("[places] failed to load:", error);
        setOutcome({ kind: "failed" });
        warnPlacesUnavailable();
      },
    );
    return () => {
      cancelled = true;
    };
  }, [shouldLoad]);

  return outcome;
}

export function PlacesProvider({
  children,
  fallback,
  deferred = false,
}: PlacesProviderProps) {
  const hasMounted = useHasMounted();
  const [isRequested, setIsRequested] = useState(!deferred);
  const outcome = usePlacesLibrary(hasMounted && isRequested);
  const requestLoad = useCallback(() => setIsRequested(true), []);

  const value = useMemo(
    () => ({
      placesLib: outcome.kind === "loaded" ? outcome.placesLib : null,
      status: resolveStatus(outcome, isRequested),
      requestLoad,
    }),
    [outcome, isRequested, requestLoad],
  );

  return (
    <ErrorBoundary label="places" fallback={<>{fallback ?? children}</>}>
      <PlacesContext value={value}>{children}</PlacesContext>
    </ErrorBoundary>
  );
}

export function usePlacesContext() {
  return use(PlacesContext);
}

export function usePlacesLib() {
  return use(PlacesContext).placesLib;
}
